import { useEffect, useMemo, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import cm from 'classnames';

import {
  StoryConfig as StoryConfigType,
  ClipOrder,
  ClipStartTimeAlignment,
  ClipTransitionAlignment,
  AspectRatioStory,
  SelectionMethod,
} from 'services/story.service';
import {
  MAX_NUMBER_OF_CHARACTERS,
  MIN_FADE_IN,
  MIN_FADE_OUT,
  PLAYBACK_RATE_MAX,
  PLAYBACK_RATE_MIN,
} from 'constants/params';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { enumToOptions } from 'components/app/_helpers/app-config.helper';
import { setStoryModal } from 'components/app/_mediator/app.slice';
import { copyStory, fetchStories, selectStory, selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { useEditorConfig } from 'components/app/editor/_mediator/editor-config/use-editor-config';
import { setTitleCredits } from 'components/app/editor/_mediator/editor-controls.slice';

import { ReactComponent as EditTextIcon } from 'assets/ui/edit-text.svg';
import { ReactComponent as ShuffleIcon } from 'assets/ui/shuffle.svg';
import { Modal } from 'components/UI/modal/modal';
import { InputGroup } from 'components/UI/input/input-group';
import { InputBase } from 'components/UI/input/elements/input-base';
import { InputArea } from 'components/UI/input/elements/input-area';
import { InputRadioGroup } from 'components/UI/input/input-radio-group';
import { Select } from 'components/UI/select/select';
import { Button } from 'components/UI/button/button';
import { Tooltip } from 'components/UI/tooltip/tooltip';
import styles from './_story-config.module.scss';
import { PaymentType, SubscriptionType } from '../../../../services/payment.service.ts';

const getClipOrderLabel = (order: ClipOrder): string => {
  switch (order) {
    case ClipOrder.chronological:
      return 'Chronologically';
    case ClipOrder.random:
      return 'Randomly';
    case ClipOrder.maintainFirstLast:
      return 'Randomly (Maintain First & Last)';
    default:
      return ClipOrder[order];
  }
};

export const StoryConfig = () => {
  const story = useAppSelector(selectStory);
  const projectId = useAppSelector((state) => state.projects.selectedProject);
  const storyModal = useAppSelector((state) => state.app.storyModal);
  const config = useAppSelector((state) => state.editor.storyConfig);
  const helpMode = useAppSelector(selectHelpMode);
  const handleConfig = useEditorConfig();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.app__data.user);

  const [order, setOrder] = useState<ClipOrder | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced'>('basic');

  // Helper functions for resolution access control
  const canUserAccessResolution = (userSubscription: SubscriptionType | null, resolution: PaymentType): boolean => {
    switch (userSubscription) {
      case null:
        return resolution === PaymentType.Hd; // Free users only get HD
      case SubscriptionType.Hd:
        return resolution <= PaymentType.Hd; // HD subscribers: HD and below
      case SubscriptionType.Fhd:
        return resolution <= PaymentType.Fhd; // FHD subscribers: FHD and below
      case SubscriptionType.Uhd:
      case SubscriptionType.Pro:
        return true; // Premium users: all resolutions
      default:
        return false;
    }
  };

  const buildResolutionOptions = (userSubscription: SubscriptionType | null) => {
    return enumToOptions(PaymentType)
      .filter((e) => e.value > 1) // Exclude Free/Basic tiers
      .map((e) => ({
        ...e,
        disabled: !canUserAccessResolution(userSubscription, e.value),
      }));
  };

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { isDirty, errors },
  } = useForm<StoryConfigType>();

  const options = useMemo(
    () => ({
      startTime: enumToOptions(ClipStartTimeAlignment),
      transition: enumToOptions(ClipTransitionAlignment),
      aspectRatio: enumToOptions(AspectRatioStory),
      resolution: buildResolutionOptions(user?.subscriptionType ?? null),
    }),
    [user]
  );

  const onReset = () => {
    setOrder(null);
    dispatch(setStoryModal(false));
  };

  const submitOrder = handleSubmit((data) => {
    handleConfig(data);
    dispatch(setStoryModal(false));
    setOrder(null);
  });

  const submitCopy = async () => {
    if (!story || !projectId) return;
    try {
      await dispatch(copyStory({ name: `${story.name} (backup)`, storyId: story.storyId }));
      await dispatch(fetchStories(projectId));
      submitOrder();
    } catch (error) {
      onReset();
    }
  };

  const onSubmitForm: SubmitHandler<StoryConfigType> = async (data) => {
    if (!config) return;
    const order = data.clipOrder !== undefined ? +data.clipOrder : config.clipOrder;
    if (config.clipOrder !== order) setOrder(order);
    else {
      handleConfig(data);
      dispatch(setStoryModal(false));
    }
  };

  useEffect(() => {
    if (!storyModal) return;
    reset();
  }, [storyModal]);

  useEffect(() => {
    if (config?.titleText) setValue('titleText', config?.titleText);
    if (config?.creditsText) setValue('creditsText', config?.creditsText);
  }, [config?.titleText, config?.creditsText]);

  return (
    <>
      <Modal isOpen={!!storyModal} onRequestClose={onReset}>
        <form className={styles.root} onSubmit={handleSubmit(onSubmitForm)} noValidate>
          <h1 className={styles.title}>Story Configuration</h1>

          {/* Tab Navigation */}
          <div className={styles.tabNavigation}>
            <button
              type="button"
              className={cm(styles.tab, { [styles.active]: activeTab === 'basic' })}
              onClick={() => setActiveTab('basic')}
            >
              Basic Settings
            </button>
            <button
              type="button"
              className={cm(styles.tab, { [styles.active]: activeTab === 'advanced' })}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced
            </button>
          </div>

          <div className={styles.body}>
            {/* Basic Settings Tab */}
            {activeTab === 'basic' && (
              <div className={styles.tabContent}>
                {/* Video Format Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Video Format</h3>
                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Video dimensions (16:9 for landscape, 9:16 for portrait/vertical video)"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Aspect Ratio</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                    >
                      <Select
                        options={options.aspectRatio}
                        defaultValue={config?.aspectRatio ?? AspectRatioStory.is16x9}
                        {...register('aspectRatio', { valueAsNumber: true })}
                      />
                    </InputGroup>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Video output quality. Higher resolutions may require a premium subscription"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Resolution</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                    >
                      <Select
                        options={options.resolution}
                        disabledOptions={true}
                        defaultValue={config?.resolution ?? PaymentType.Hd}
                        {...register('resolution', { valueAsNumber: true })}
                      />
                    </InputGroup>
                  </div>
                </div>

                {/* Title & Credits Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Title & Credits</h3>
                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Text displayed at the beginning of your video"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Title Text</span>
                        </Tooltip>
                      }
                      className={cm(styles.cell, styles['cell--title-credits'])}
                      error={errors.titleText?.message}
                    >
                      <InputBase
                        defaultValue={config?.titleText ?? ''}
                        {...register('titleText', {
                          maxLength: {
                            value: MAX_NUMBER_OF_CHARACTERS,
                            message: `The maximum allowable number of characters is '${MAX_NUMBER_OF_CHARACTERS}'`,
                          },
                        })}
                        alert={!!errors.titleText?.message}
                      />
                      <EditTextIcon
                        onClick={() => dispatch(setTitleCredits({ key: 'title', data: { ...config, ...getValues() } }))}
                      />
                    </InputGroup>
                  </div>

                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip tip="Text displayed at the end of your video" showTooltip={helpMode} dynamicPosition>
                          <span>Credits Text</span>
                        </Tooltip>
                      }
                      className={cm(styles.cell, styles['cell--title-credits'])}
                      error={errors.creditsText?.message}
                    >
                      <InputArea
                        defaultValue={config?.creditsText ?? ''}
                        {...register('creditsText', {
                          maxLength: {
                            value: MAX_NUMBER_OF_CHARACTERS,
                            message: `The maximum allowable number of characters is '${MAX_NUMBER_OF_CHARACTERS}'`,
                          },
                        })}
                        alert={!!errors.creditsText?.message}
                      />
                      <EditTextIcon
                        onClick={() =>
                          dispatch(setTitleCredits({ key: 'credits', data: { ...config, ...getValues() } }))
                        }
                      />
                    </InputGroup>
                  </div>
                </div>

                {/* Clip Organization Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Clip Organization</h3>
                  <div className={styles.row}>
                    <InputRadioGroup
                      id="selectionMethod"
                      header={
                        <Tooltip
                          tip="How highlights are selected: auto-detected or manually selected"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Selection Method</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                      defaultValue={config?.selectionMethod}
                      data={[
                        { label: 'Auto Detect', value: SelectionMethod.autoDetect },
                        { label: 'Manual', value: SelectionMethod.manual },
                      ]}
                      {...register('selectionMethod')}
                    />
                    <InputRadioGroup
                      id="clipOrder"
                      header={
                        <Tooltip
                          tip="Shuffles existing storyboard clips upon save. New clips added later are not affected by this action."
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <div className={styles.headerWithIcon}>
                            <ShuffleIcon />
                            <span>Shuffle Clips</span>
                          </div>
                        </Tooltip>
                      }
                      className={styles.cell}
                      defaultValue={config?.clipOrder}
                      data={[
                        { label: 'Chronologically', value: ClipOrder.chronological },
                        { label: 'Randomly', value: ClipOrder.random },
                        { label: 'Randomly (Maintain First & Last)', value: ClipOrder.maintainFirstLast },
                      ]}
                      {...register('clipOrder')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Settings Tab */}
            {activeTab === 'advanced' && (
              <div className={styles.tabContent}>
                {/* Timing Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Timing</h3>
                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Speed multiplier for video playback (1.0 = normal speed, <1 = slower, >1 = faster)"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Playback Rate</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                      error={errors.playbackRate?.message}
                    >
                      <InputBase
                        defaultValue={config?.playbackRate ?? ''}
                        {...register('playbackRate', {
                          required: true,
                          valueAsNumber: true,
                          max: {
                            value: PLAYBACK_RATE_MAX,
                            message: `The maximum allowable playback rate is '${PLAYBACK_RATE_MAX}'`,
                          },
                          min: {
                            value: PLAYBACK_RATE_MIN,
                            message: `The minimum allowable playback rate is '${PLAYBACK_RATE_MIN}'`,
                          },
                        })}
                        type="number"
                        alert={!!errors.playbackRate?.message}
                      />
                    </InputGroup>
                    <div className={styles.cell}></div>
                  </div>

                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Fade-in duration at the start of the storyboard in milliseconds"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Track Fade-In Time (ms)</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                      error={errors.trackFadeInTime?.message}
                    >
                      <InputBase
                        defaultValue={config?.trackFadeInTime ?? ''}
                        {...register('trackFadeInTime', {
                          required: true,
                          valueAsNumber: true,
                          min: {
                            value: MIN_FADE_IN,
                            message: `The minimum allowable track fade-in time is ${MIN_FADE_IN}`,
                          },
                        })}
                        type="number"
                        alert={!!errors.trackFadeInTime?.message}
                      />
                    </InputGroup>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Fade-out duration at the end of the storyboard in milliseconds"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Track Fade-Out Time (ms)</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                      error={errors.trackFadeOutTime?.message}
                    >
                      <InputBase
                        defaultValue={config?.trackFadeOutTime ?? ''}
                        {...register('trackFadeOutTime', {
                          required: true,
                          valueAsNumber: true,
                          min: {
                            value: MIN_FADE_OUT,
                            message: `The minimum allowable track fade-out time is ${MIN_FADE_OUT}`,
                          },
                        })}
                        type="number"
                        alert={!!errors.trackFadeOutTime?.message}
                      />
                    </InputGroup>
                  </div>

                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="How long the title text displays on screen in milliseconds"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Title Duration (ms)</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                    >
                      <InputBase
                        defaultValue={config?.titleDuration ?? ''}
                        type="number"
                        {...register('titleDuration', { required: true, valueAsNumber: true })}
                      />
                    </InputGroup>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="How long the credits text displays on screen in milliseconds"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Credits Duration (ms)</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                    >
                      <InputBase
                        defaultValue={config?.creditsDuration ?? ''}
                        type="number"
                        {...register('creditsDuration', { required: true, valueAsNumber: true })}
                      />
                    </InputGroup>
                  </div>
                </div>

                {/* Transitions Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Transitions</h3>
                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Length of transitions between clips in milliseconds"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Transition Duration (ms)</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                    >
                      <InputBase
                        defaultValue={config?.transitionTime ?? ''}
                        {...register('transitionTime', { required: true, valueAsNumber: true })}
                        type="number"
                      />
                    </InputGroup>
                    <div className={styles.cell}></div>
                  </div>

                  <div className={styles.row}>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Determines where clips start: aligned to audio beats or at the beginning of transitions"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Clip Start Time Alignment</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                    >
                      <Select
                        options={options.startTime}
                        defaultValue={config?.clipStartTimeAlignment ?? ClipStartTimeAlignment.TransitionStart}
                        {...register('clipStartTimeAlignment', { valueAsNumber: true })}
                      />
                    </InputGroup>
                    <InputGroup
                      header={
                        <Tooltip
                          tip="Position of the clip transition relative to the audio beat: leading edge, centered, or trailing edge"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span>Clip Transition Alignment</span>
                        </Tooltip>
                      }
                      className={styles.cell}
                    >
                      <Select
                        options={options.transition}
                        defaultValue={config?.clipTransitionAlignment ?? ClipTransitionAlignment.Centered}
                        {...register('clipTransitionAlignment', { valueAsNumber: true })}
                      />
                    </InputGroup>
                  </div>
                </div>

                {/* Options Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Options</h3>
                  <div className={styles.row}>
                    <div className={styles.cell}>
                      <div className={styles.inlineHeader}>
                        <InputBase
                          type="checkbox"
                          defaultChecked={config?.snapToBeats ?? true}
                          className={styles.smallCheckbox}
                          {...register('snapToBeats')}
                        />
                        <Tooltip
                          tip="Enable beat-snapping alignment on clip resize action"
                          showTooltip={helpMode}
                          dynamicPosition
                        >
                          <span className={styles.headerText}>Snap to Beats</span>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <Button outlined onClick={onReset} type="button">
              {storyModal === 'setup' ? 'Use Default Settings' : 'Cancel'}
            </Button>
            <Button
              disabled={!isDirty}
              type="submit"
              className={cm(styles.primaryButton, { [styles.primaryButtonDisabled]: !isDirty })}
            >
              {storyModal === 'setup' ? 'Save' : 'Save Configuration'}
            </Button>
          </div>
        </form>
      </Modal>
      <Modal className={styles.root} onRequestClose={() => setOrder(null)} isOpen={order !== null}>
        <h3 className={styles.title}>
          Storyboard clips will be shuffled{' '}
          <span>
            <span>{order !== null && getClipOrderLabel(order)}</span>
          </span>
          .<p>Do you want to create a backup of this story before reordering?</p>
        </h3>
        <div className={styles.footer}>
          <Button onClick={() => setOrder(null)} outlined>
            Cancel
          </Button>
          <Button onClick={submitOrder} outlined>
            No
          </Button>
          <Button onClick={submitCopy}>Yes</Button>
        </div>
      </Modal>
    </>
  );
};
